import { useEffect } from 'react';

/**
 * Keep a route mounted while a one-time response is pending or visible.
 *
 * The caller owns the boolean and any sensitive data. This hook only captures
 * the current React Router history index, so it never persists response data
 * and never pushes sentinel entries that could remain after unlocking.
 */
export function useBrowserNavigationGuard(locked: boolean): void {
  useEffect(() => {
    if (!locked || typeof window === 'undefined') return;

    const lockedHistoryIndex =
      (window.history.state as { idx?: unknown } | null)?.idx;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      event.stopImmediatePropagation();

      const nextHistoryIndex = (event.state as { idx?: unknown } | null)?.idx;
      if (typeof lockedHistoryIndex === 'number' && typeof nextHistoryIndex === 'number') {
        const restoreDelta = lockedHistoryIndex - nextHistoryIndex;
        if (restoreDelta !== 0) window.history.go(restoreDelta);
        return;
      }

      // Defensive fallback for non-React-Router entries. The guarded user
      // action is Back, so Forward restores the route without adding history.
      window.history.forward();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState, true);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [locked]);
}
